import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { Phone, Mail, User, UserCheck, XCircle, Users, ClipboardList, PlusCircle, ShieldBan, Trash2, GitMerge, ChevronDown, Paperclip, Search, X, Download } from 'lucide-react';
import { usePageContext } from '@/hooks/usePageContext';
import { useAISidebarStore } from '@/store/aiSidebarStore';
import {
  Badge,
  Card,
  CardHeader,
  CardBody,
  EmptyState,
  Spinner,
} from '@/components/ui';
import * as leadsSvc from '@/services/leads.service';
import type { LeadMatchCandidate } from '@/services/leads.service';
import { searchPatients } from '@/services/patients.service';
import type { PatientSummary } from '@/types/patient.types';
import { apiClient } from '@/services/apiClient';
import { splitRawContent, stripHtmlToText, parseDateValue } from '@/lib/utils';
import { LeadMergeDialog } from './LeadMergeDialog';
import { LeadFormNotice, LeadFormAttachConfirm, type FormAttachPatient } from './LeadFormAttach';
import { formatMarketingSource } from './marketingSource';

/**
 * Renders the lead's Date of Birth. Parsed via `parseDateValue` so a date-only
 * 'YYYY-MM-DD' is read as LOCAL midnight: a bare `new Date(dateStr)` lands on UTC
 * midnight, which in a negative-offset zone renders as the previous calendar day
 * (a stored 1965-07-22 displayed here as July 21 while the DOB in the message
 * content \u2014 the answer as submitted \u2014 correctly read July 22). A DOB is a plain
 * calendar date and must never be timezone-converted.
 */
function formatDate(dateStr: string | null | undefined): string {
  const date = parseDateValue(dateStr);
  if (!date) return '\u2014';
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}


function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}



function hasHtmlContent(value: string | null | undefined): boolean {
  if (!value) return false;
  return /<[a-z][\s\S]*>/i.test(value) || /<!doctype/i.test(value) || /<html[\s>]/i.test(value);
}

function hasDetailValue(value: string | number | boolean | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '' && value.trim() !== '\u2014';
  return true;
}

interface LeadInfoField {
  label: string;
  value: string | number | null | undefined;
  /** Rendered after the value, e.g. a marker badge. Never shown without a value. */
  extra?: ReactNode;
}

/**
 * The lead's body: details, message, attachments, patient matches. The header
 * (status, name, actions) is LeadDetailFrame's, so the modal and the page show
 * the same lead the same way. `mode` only picks where "not found" sends you.
 */
export interface LeadDetailViewProps {
  leadId: number;
  mode?: 'page' | 'modal';
  onClose?: () => void;
  onPatientSelect?: (patient: { patientId: number; displayId: string | null; firstName: string; lastName: string; email: string | null; cellPhone: string | null; birthDate: string | null } | null) => void;
  selectedPatientId?: number | null;
  onMergeCommLog?: () => void;
}

export function LeadDetailView({
  leadId,
  mode = 'page',
  onClose,
  onPatientSelect,
  selectedPatientId,
  onMergeCommLog,
}: LeadDetailViewProps) {
  const navigate = useNavigate();
  const updateAIContext = usePageContext('Lead Detail');
  const setAISidebarContext = useAISidebarStore((s) => s.setContext);

  const {
    data: lead,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => leadsSvc.getLead(leadId),
    enabled: !isNaN(leadId),
  });

  useEffect(() => {
    if (!lead) return;
    const name = `${lead.firstName ?? ''} ${lead.lastName ?? ''}`.trim() || `Lead #${leadId}`;
    updateAIContext({ dataSummary: `Viewing lead: ${name} (ID ${leadId}, status: ${lead.status ?? 'unknown'})` });
  }, [lead, leadId, updateAIContext]);

  // Focus the assistant on this lead so LeadContextPanel and the lead-specific
  // suggestions apply. Must run after usePageContext above, which clears the
  // entity context on mount/route change.
  useEffect(() => {
    if (!isNaN(leadId) && leadId > 0) {
      setAISidebarContext('lead', leadId);
    }
  }, [leadId, setAISidebarContext]);

  // Scored "Possible Matches" — legacy parity with
  // message-lead-display-fs.php L696-996 (GetSimilarWebLeadNamesConvert plus
  // per-row field-match scoring). Drives both the sidebar matches list and
  // the LeadMergeDialog's Step-1 pre-population.
  const {
    data: matchCandidates,
    isLoading: matchCandidatesLoading,
  } = useQuery({
    queryKey: ['lead-match-candidates', leadId],
    queryFn: () => leadsSvc.getLeadMatchCandidates(leadId, 'email'),
    enabled: !isNaN(leadId),
  });

  const [mergeOpen, setMergeOpen] = useState(false);
  // When set, the merge dialog pre-selects this patient and skips Step 1.
  // Drives the sidebar's per-row Merge affordance (legacy parity:
  // message-lead-display-fs.php L884 `loadPlanMergeList(ID, ...)`).
  const [mergeInitialPatient, setMergeInitialPatient] = useState<{
    patientId: number;
    displayId: string | null;
    firstName: string;
    lastName: string;
    email: string | null;
    cellPhone: string | null;
    birthDate: string | null;
  } | null>(null);

  // Patient search state for the "Patient Matches" section
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [patientSearchResults, setPatientSearchResults] = useState<PatientSummary[]>([]);
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);
  const [showPatientSearchResults, setShowPatientSearchResults] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string } | null>(null);
  // The patient chosen to receive the lead's completed form; opens the confirm step.
  const [formAttachPatient, setFormAttachPatient] = useState<FormAttachPatient | null>(null);

  const handlePatientSearch = useCallback((value: string) => {
    setPatientSearchTerm(value);
    if (value.trim() === '') {
      setPatientSearchResults([]);
      setShowPatientSearchResults(false);
    }
  }, []);

  const executePatientSearch = useCallback(async () => {
    const term = patientSearchTerm.trim();
    if (term.length < 2) return;
    setPatientSearchLoading(true);
    try {
      const results = await searchPatients(term);
      setPatientSearchResults(results);
      setShowPatientSearchResults(true);
    } catch {
      setPatientSearchResults([]);
    } finally {
      setPatientSearchLoading(false);
    }
  }, [patientSearchTerm]);

  const handlePatientSearchSelect = useCallback((patient: PatientSummary) => {
    const selected = {
      patientId: patient.patientId,
      displayId: patient.displayId,
      firstName: patient.firstName,
      lastName: patient.lastName,
      email: patient.email,
      cellPhone: patient.cellPhone ?? patient.phone ?? null,
      birthDate: patient.dateOfBirth,
    };
    if (onPatientSelect) {
      onPatientSelect(selected);
    } else {
      setMergeInitialPatient(selected);
      setMergeOpen(true);
    }
  }, [onPatientSelect]);

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError || !lead) {
    return (
      <EmptyState
        title="Lead not found"
        description="This lead could not be loaded. It may have been deleted or you may not have access."
        action={{
          label: mode === 'modal' ? 'Close' : 'Back to Leads',
          onClick: mode === 'modal' ? (onClose ?? (() => undefined)) : () => navigate('/leads'),
        }}
      />
    );
  }

  const isConverted = lead.status === 'converted';
  const isDead = lead.status === 'dead' || lead.status === 'spam' || lead.status === 'trash';
  const isSpam = lead.status === 'spam';
  const isTrash = lead.status === 'trash';
  const eform = lead.eform ?? null;
  // Same gate as Merge to Comm Log: a closed lead offers no attach action.
  const canAttachForm = eform !== null && eform.ownerPatientId === null && !isConverted && !isDead;

  // Prefer the backend-split preview; fall back to client-side split for any older
  // cached payload that only has `message`. Legacy never wants the raw half inline.
  const rawSplit = splitRawContent(lead.message);
  const messagePreview = lead.messagePreview ?? rawSplit.preview;
  const messageRaw = lead.messageRaw ?? rawSplit.raw;
  const hasRawContent = lead.hasRawContent ?? rawSplit.hasRaw;
  const previewText = stripHtmlToText(messagePreview);
  const renderPreviewAsHtml = hasHtmlContent(messagePreview);
  const separateNotes =
    lead.notes && lead.notes !== lead.message && lead.notes !== messagePreview
      ? lead.notes
      : null;

  // Status banner (legacy .section-notification at message-lead-display-fs.php:252-270).
  let statusBanner: { icon: JSX.Element; text: string; tone: string } | null = null;
  if (isSpam) {
    statusBanner = {
      icon: <ShieldBan className="h-4 w-4" />,
      text: lead.convertedByName ? `Marked Spam by ${lead.convertedByName}` : 'Marked Spam',
      tone: 'bg-red-100 text-red-800 border-red-300',
    };
  } else if (isTrash) {
    statusBanner = {
      icon: <Trash2 className="h-4 w-4" />,
      text: lead.convertedByName ? `Marked Trash by ${lead.convertedByName}` : 'Marked Trash',
      tone: 'bg-red-100 text-red-800 border-red-300',
    };
  } else if (isConverted && lead.convertedByName) {
    statusBanner = {
      icon: <UserCheck className="h-4 w-4" />,
      text: `Converted by ${lead.convertedByName}`,
      tone: 'bg-green-50 text-green-700 border-green-200',
    };
  } else if (isDead && lead.badReason) {
    statusBanner = {
      icon: <XCircle className="h-4 w-4" />,
      text: `Marked Bad: ${lead.badReason}`,
      tone: 'bg-red-50 text-red-700 border-red-200',
    };
  }

  const contactsRecord = lead.contactsRecord ?? [];
  const contactsAttachments = lead.contactsAttachments ?? [];

  // Structured field columns — legacy style
  const leftColumnFields: LeadInfoField[] = [
    { label: 'First Name', value: lead.firstName },
    { label: 'Last Name', value: lead.lastName },
    { label: 'Phone', value: formatPhone(lead.cellPhone || lead.phone) || null },
    { label: 'Email', value: lead.email },
    { label: 'Date of Birth', value: lead.birthDate ? formatDate(lead.birthDate) : null },
  ].filter((f) => hasDetailValue(f.value));

  const rightColumnFields: LeadInfoField[] = [
    { label: 'Address', value: lead.address },
    { label: 'City', value: lead.city },
    { label: 'State', value: lead.state },
    { label: 'ZIP', value: lead.zip && lead.zip !== '0' ? lead.zip : null },
  ].filter((f) => hasDetailValue(f.value));

  const sourceInfoFields: LeadInfoField[] = [
    { label: 'Subject', value: lead.subject },
    { label: 'Practice Referral', value: lead.practiceReferral },
    { label: 'Lead Source', value: lead.leadSourceName },
    // Request-info leads only: the campaign or automation step that delivered
    // the link, from the lead's attribution record.
    {
      label: 'Marketing source',
      value: lead.marketingSource ? formatMarketingSource(lead.marketingSource) : null,
      extra: lead.marketingSource?.referral ? (
        <Badge variant="purple" size="sm" className="ml-1 align-middle">
          Referral
        </Badge>
      ) : undefined,
    },
    { label: 'Originating Site', value: lead.siteReferrer },
    { label: 'Referring URL', value: lead.referUrl },
    { label: 'Search Terms', value: lead.referKeywords },
  ].filter((f) => hasDetailValue(f.value));

  const hasMessage = hasDetailValue(lead.message);
  const hasWebLeadInfo = leftColumnFields.length > 0 || rightColumnFields.length > 0 || sourceInfoFields.length > 0 || contactsRecord.length > 0 || hasMessage || !!separateNotes || hasRawContent;

  return (
    <div className="space-y-5">
      {statusBanner && (
        <div
          className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium ${statusBanner.tone}`}
        >
          {statusBanner.icon}
          <span>{statusBanner.text}</span>
          {lead.mergedPatientId !== null && lead.mergedPatientId !== undefined && (
            <Link
              to={`/patients/${lead.mergedPatientId}`}
              className="ml-auto text-xs font-medium underline-offset-2 hover:underline"
            >
              View patient
            </Link>
          )}
        </div>
      )}

      {eform && (
        <LeadFormNotice eform={eform} canAttach={canAttachForm} onChoosePatient={setFormAttachPatient} />
      )}

      <div className={`grid grid-cols-1 gap-5 ${lead.status === 'new' ? 'xl:grid-cols-3' : ''}`}>
        <div className={`space-y-4 ${lead.status === 'new' ? 'xl:col-span-2' : ''}`}>
          {hasWebLeadInfo && (
            <Card noPadding>
              <div className="px-5 py-4">
                <CardBody>
                  {(leftColumnFields.length > 0 || rightColumnFields.length > 0) && (
                    <>
                      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                        <User className="h-3.5 w-3.5 text-blue-500" /> Contact Details
                      </h3>
                      <div className="pl-5 grid grid-cols-1 gap-x-8 md:grid-cols-2">
                        {leftColumnFields.length > 0 && (
                          <div className="space-y-1">
                            {leftColumnFields.map((field) => (
                              <div key={field.label} className="flex items-baseline gap-2">
                                <dt className="w-20 shrink-0 text-right text-[11px] text-slate-500">{field.label}</dt>
                                <dd className="text-sm text-slate-800">{field.value}</dd>
                              </div>
                            ))}
                          </div>
                        )}
                        {rightColumnFields.length > 0 && (
                          <div className="space-y-1">
                            {rightColumnFields.map((field) => (
                              <div key={field.label} className="flex items-baseline gap-2">
                                <dt className="w-20 shrink-0 text-right text-[11px] text-slate-500">{field.label}</dt>
                                <dd className="text-sm text-slate-800">{field.value}</dd>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {contactsRecord.length > 0 && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                        <ClipboardList className="h-3.5 w-3.5 text-amber-500" /> Additional Info
                      </h3>
                      <div className="pl-5 grid grid-cols-2 gap-x-6 gap-y-3 overflow-hidden">
                        {contactsRecord.map((row, idx) => (
                          <div key={`${row.label}-${idx}`} className="min-w-0">
                            <dt className="text-[11px] font-medium text-slate-500">{row.label || '—'}</dt>
                            <dd className="text-sm text-slate-800 break-all">{row.value}</dd>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {sourceInfoFields.length > 0 && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                        <Mail className="h-3.5 w-3.5 text-emerald-500" /> Source Info
                      </h3>
                      <div className="pl-5 space-y-1">
                        {sourceInfoFields.map((field) => (
                          <div key={field.label} className="text-sm">
                            <span className="text-[11px] text-slate-500">{field.label}:</span>{' '}
                            <span className="text-slate-800 break-all">{field.value}</span>
                            {field.extra}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {contactsAttachments.length > 0 && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                        <Paperclip className="h-3.5 w-3.5 text-rose-500" /> Attachments
                      </h3>
                      <ul className="pl-5 space-y-0.5">
                        {contactsAttachments.map((att) => (
                          <li key={att.id}>
                            <button
                              type="button"
                              onClick={() => setPreviewFile({ url: att.downloadUrl.replace(/^\/api\/v5/, ''), name: att.filename })}
                              className="inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-sm text-indigo-700 hover:bg-indigo-50 cursor-pointer"
                            >
                              <Paperclip className="h-3 w-3" />
                              {att.filename}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {hasRawContent && hasDetailValue(messageRaw) && (
                    <details open className="mt-3 rounded-md border border-slate-200 bg-slate-50">
                      <summary className="flex cursor-pointer items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 hover:text-slate-900 [&::-webkit-details-marker]:hidden">
                        <ChevronDown className="h-3.5 w-3.5 transition-transform [details[open]>&]:rotate-180" />
                        Original email
                      </summary>
                      <div className="border-t border-slate-200 bg-white p-2">
                        <iframe
                          title="Original email"
                          sandbox=""
                          referrerPolicy="no-referrer"
                          className="w-full min-h-[420px] rounded border border-slate-200 bg-white"
                          srcDoc={DOMPurify.sanitize(messageRaw ?? '', { WHOLE_DOCUMENT: true })}
                        />
                      </div>
                    </details>
                  )}

                  {!hasRawContent && hasMessage && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                        <Mail className="h-3.5 w-3.5 text-violet-500" /> Message
                      </h3>
                      {renderPreviewAsHtml ? (
                        <dd
                          className="text-sm text-slate-800 [&_a]:text-indigo-600 [&_a]:underline [&_a]:cursor-pointer [&_table]:w-full [&_td]:p-1.5 [&_td]:text-sm [&_td]:border [&_td]:border-slate-200"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(messagePreview, { ADD_ATTR: ['target'] }) }}
                          onClick={(e) => {
                            const anchor = (e.target as HTMLElement).closest('a');
                            if (!anchor) return;
                            const href = anchor.getAttribute('href') ?? '';
                            if (href.includes('/attachments/download')) {
                              e.preventDefault();
                              const params = new URLSearchParams(href.split('?')[1] ?? '');
                              const apiPath = href.replace(/^\/api\/v5/, '');
                              setPreviewFile({ url: apiPath, name: params.get('name') ?? 'file' });
                            } else if (href.includes('showfile_contact.php')) {
                              e.preventDefault();
                              const params = new URLSearchParams(href.split('?')[1] ?? '');
                              const file = params.get('file') ?? '';
                              const contactID = params.get('contactID') ?? '';
                              if (file && contactID) {
                                setPreviewFile({ url: `/leads/${contactID}/attachments/download?name=${encodeURIComponent(file)}&inline=true`, name: file });
                              }
                            }
                          }}
                        />
                      ) : (
                        <dd className="whitespace-pre-line text-sm text-slate-800">{lead.message}</dd>
                      )}
                    </div>
                  )}

                  {separateNotes && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <dt className="text-[11px] text-slate-400">Notes</dt>
                      <dd className="mt-0.5 whitespace-pre-line text-sm text-slate-800">{separateNotes}</dd>
                    </div>
                  )}
                </CardBody>
              </div>
            </Card>
          )}

        </div>

        <div className="sticky top-0 max-h-[calc(80vh-4rem)] self-start space-y-4 overflow-y-auto">
          {lead.status === 'new' && (
          <Card noPadding>
            <div className="px-3 py-3">
              <CardHeader>
                <h2 className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                  <Users className="h-3.5 w-3.5 text-blue-500" />
                  Patient Matches
                </h2>
              </CardHeader>
              <CardBody className="max-h-[28rem] overflow-y-auto">
                {!isConverted && !isDead && (
                  <div className="mb-3">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search for patient..."
                        value={patientSearchTerm}
                        onChange={(e) => handlePatientSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void executePatientSearch();
                          }
                        }}
                        className="w-full rounded-md border border-slate-200 py-1.5 pl-7 pr-2 text-sm placeholder:text-slate-400 focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-300"
                      />
                      {patientSearchLoading && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          <div className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500" />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Search results displayed as cards */}
                {showPatientSearchResults && patientSearchResults.length > 0 && (
                  <ul className="mb-3 flex flex-col gap-1.5">
                    {[...patientSearchResults]
                      .sort((a, b) => {
                        if (a.patientId === selectedPatientId) return -1;
                        if (b.patientId === selectedPatientId) return 1;
                        return 0;
                      })
                      .map((p) => {
                        const isSelected = p.patientId === selectedPatientId;
                        return (
                          <li
                            key={p.patientId}
                            className={`rounded-md border p-2 ${isSelected ? 'border-emerald-300 bg-emerald-50' : 'cursor-pointer border-slate-200 bg-slate-50 hover:bg-slate-100'}`}
                            onClick={() => {
                              if (!isSelected) handlePatientSearchSelect(p);
                            }}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <Link
                                to={`/patients/${p.patientId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`text-sm font-semibold ${isSelected ? 'text-emerald-800' : 'text-blue-700'} hover:underline`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {p.lastName}, {p.firstName}
                                {p.displayId ? ` (#${p.displayId})` : ''}
                              </Link>
                              {isSelected ? (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); onPatientSelect?.(null); }}
                                  className="rounded p-0.5 text-emerald-600 hover:bg-emerald-100"
                                  title="Unselect patient"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              ) : (
                                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">Select patient</span>
                              )}
                            </div>
                            <div className="mt-1 flex flex-col gap-0.5 text-xs text-slate-600">
                              {p.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" /> {p.email}
                                </span>
                              )}
                              {(p.cellPhone || p.phone) && (
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" /> {formatPhone(p.cellPhone ?? p.phone)}
                                </span>
                              )}
                              {p.dateOfBirth && (
                                <span>DOB {p.dateOfBirth}</span>
                              )}
                            </div>
                            {isSelected && (
                              <div className="mt-2 flex items-center gap-1.5 border-t border-emerald-200 pt-2">
                                <button
                                  type="button"
                                  className="inline-flex whitespace-nowrap items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-blue-700"
                                  onClick={(e) => { e.stopPropagation(); onMergeCommLog ? onMergeCommLog() : setMergeOpen(true); }}
                                >
                                  <GitMerge className="h-3 w-3" />
                                  Merge to Comm Log
                                </button>
                                <button
                                  type="button"
                                  className="inline-flex whitespace-nowrap items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
                                  onClick={(e) => { e.stopPropagation(); navigate(`/leads/new?leadId=${leadId}&patientId=${p.patientId}`); }}
                                >
                                  <PlusCircle className="h-3 w-3" />
                                  New Plan
                                </button>
                              </div>
                            )}
                          </li>
                        );
                      })}
                  </ul>
                )}
                {showPatientSearchResults && patientSearchResults.length === 0 && !patientSearchLoading && (
                  <p className="mb-3 text-center text-xs text-slate-400">No patients found</p>
                )}

                {/* Auto-matched candidates — hidden when search results are showing */}
                {!showPatientSearchResults && (matchCandidatesLoading ? (
                  <div className="flex justify-center py-3">
                    <Spinner size="sm" />
                  </div>
                ) : matchCandidates && matchCandidates.length > 0 ? (
                  <ul className="flex flex-col gap-1.5">
                    {[...matchCandidates]
                      .sort((a, b) => {
                        if (a.patientId === selectedPatientId) return -1;
                        if (b.patientId === selectedPatientId) return 1;
                        return 0;
                      })
                      .map((c) => (
                      <SimilarNameRow
                        key={c.patientId}
                        candidate={c}
                        canMerge={!isConverted && !isDead}
                        selected={c.patientId === selectedPatientId}
                        onUnselect={() => onPatientSelect?.(null)}
                        onMergeCommLog={() => { onMergeCommLog ? onMergeCommLog() : setMergeOpen(true); }}
                        onAddPlan={() => navigate(`/leads/new?leadId=${leadId}&patientId=${c.patientId}`)}
                        onAddFormToChart={
                          canAttachForm
                            ? () =>
                                setFormAttachPatient({
                                  patientId: c.patientId,
                                  firstName: c.firstName,
                                  lastName: c.lastName,
                                  dateOfBirth: c.birthDate,
                                })
                            : undefined
                        }
                        onMerge={() => {
                          const patient = {
                            patientId: c.patientId,
                            displayId: c.displayId != null ? String(c.displayId) : null,
                            firstName: c.firstName,
                            lastName: c.lastName,
                            email: c.email,
                            cellPhone: c.cellPhone,
                            birthDate: c.birthDate,
                          };
                          if (onPatientSelect) {
                            onPatientSelect(patient);
                          } else {
                            setMergeInitialPatient(patient);
                            setMergeOpen(true);
                          }
                        }}
                      />
                    ))}
                  </ul>
                ) : (
                  <p className="py-3 text-center text-xs text-slate-400">
                    No matching patients found
                  </p>
                ))}
              </CardBody>
            </div>
          </Card>
          )}

        </div>
      </div>

      {!onMergeCommLog && (
        <LeadMergeDialog
          isOpen={mergeOpen}
          onClose={() => {
            setMergeOpen(false);
            setMergeInitialPatient(null);
          }}
          leadId={leadId}
          leadKind="email"
          subjectPreview={lead.subject ?? previewText}
          initialPatient={mergeInitialPatient}
        />
      )}

      {eform && (
        <LeadFormAttachConfirm
          leadId={leadId}
          eform={eform}
          patient={formAttachPatient}
          onClose={() => setFormAttachPatient(null)}
        />
      )}

      {previewFile && (
        <AttachmentPreviewModal
          url={previewFile.url}
          name={previewFile.name}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>
  );
}

function AttachmentPreviewModal({ url, name, onClose }: { url: string; name: string; onClose: () => void }) {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'tif'].includes(ext);
  const isPdf = ext === 'pdf';
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', handler); };
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get(url, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(res.data as Blob);
        setBlobUrl(objectUrl);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [url]);

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = name;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] flex flex-col rounded-lg bg-white shadow-xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 flex-shrink-0">
          <h3 className="text-sm font-semibold text-slate-900 truncate">{name}</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownload}
              disabled={!blobUrl}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" />
              Download
            </button>
            <button type="button" onClick={onClose} className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center p-4 min-h-[300px]">
          {loading ? (
            <Spinner className="h-8 w-8 text-slate-400" />
          ) : blobUrl && isImage ? (
            <img src={blobUrl} alt={name} className="max-w-full max-h-[75vh] object-contain rounded shadow-lg" />
          ) : blobUrl && isPdf ? (
            <iframe src={blobUrl} title={name} className="w-full h-[75vh] rounded border border-slate-200" />
          ) : (
            <div className="text-center space-y-3">
              <p className="text-sm text-slate-500">Preview not available for this file type.</p>
              <button
                type="button"
                onClick={handleDownload}
                disabled={!blobUrl}
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                Download File
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Similar Names sidebar row ───────────────────────────────────────────────
//
// Mirrors the per-row layout from legacy message-lead-display-fs.php L939-994:
// color-tinted background by match score, per-field check marks for matching
// signals, link to patient details, and a "Merge" affordance that opens
// LeadMergeDialog pre-selected to this patient.

function SimilarNameRow({
  candidate,
  canMerge,
  onMerge,
  selected,
  onUnselect,
  onMergeCommLog,
  onAddPlan,
  onAddFormToChart,
}: {
  candidate: LeadMatchCandidate;
  canMerge: boolean;
  onMerge: () => void;
  selected?: boolean;
  onUnselect?: () => void;
  onMergeCommLog?: () => void;
  onAddPlan?: () => void;
  /** Set only while the lead's completed form can be added to a chart. */
  onAddFormToChart?: () => void;
}) {
  const bgClass = selected ? 'bg-emerald-50 border-emerald-300' : `${sidebarMatchBackground(candidate.matchScore)} border-slate-200`;
  const lastFirst = [candidate.lastName, candidate.firstName].filter(Boolean).join(', ') || 'Unknown';
  return (
    <li
      className={`rounded-md border p-2 ${bgClass} ${!selected ? 'cursor-pointer' : ''}`}
      onClick={() => {
        if (!selected) onMerge();
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <Link
          to={`/patients/${candidate.patientId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-sm font-semibold ${selected ? 'text-emerald-800' : 'text-blue-700'} hover:underline`}
          onClick={(e) => e.stopPropagation()}
        >
          {lastFirst}
          {candidate.displayId ? ` (#${candidate.displayId})` : ''}
        </Link>
        {selected ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onUnselect?.(); }}
            className="rounded p-0.5 text-emerald-600 hover:bg-emerald-100"
            title="Unselect patient"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : canMerge ? (
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">Select patient</span>
        ) : null}
      </div>
      <div className="mt-1 flex flex-col gap-0.5 text-xs text-slate-600">
        {candidate.email && (
          <span className={candidate.matches.email ? 'flex items-center gap-1 font-semibold text-emerald-700' : 'flex items-center gap-1'}>
            <Mail className="h-3 w-3" /> {candidate.email}
          </span>
        )}
        {(candidate.cellPhone || candidate.homePhone) && (
          <span className={candidate.matches.phone ? 'flex items-center gap-1 font-semibold text-emerald-700' : 'flex items-center gap-1'}>
            <Phone className="h-3 w-3" /> {formatPhone(candidate.cellPhone ?? candidate.homePhone)}
          </span>
        )}
        {candidate.birthDate && (
          <span className={candidate.matches.dob ? 'font-semibold text-emerald-700' : ''}>
            DOB {candidate.birthDate}
          </span>
        )}
        {candidate.city && candidate.state && (
          <span>{candidate.city}, {candidate.state}</span>
        )}
      </div>
      {onAddFormToChart && (
        <div className="mt-2">
          <button
            type="button"
            className="inline-flex whitespace-nowrap items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
            onClick={(e) => { e.stopPropagation(); onAddFormToChart(); }}
          >
            <ClipboardList className="h-3 w-3" />
            Add form to chart
          </button>
        </div>
      )}
      {selected && (
        <div className="mt-2 flex items-center gap-1.5 border-t border-emerald-200 pt-2">
          <button
            type="button"
            className="inline-flex whitespace-nowrap items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-blue-700"
            onClick={(e) => { e.stopPropagation(); onMergeCommLog?.(); }}
          >
            <GitMerge className="h-3 w-3" />
            Merge to Comm Log
          </button>
          <button
            type="button"
            className="inline-flex whitespace-nowrap items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
            onClick={(e) => { e.stopPropagation(); onAddPlan?.(); }}
          >
            <PlusCircle className="h-3 w-3" />
            New Plan
          </button>
        </div>
      )}
    </li>
  );
}

function sidebarMatchBackground(score: number): string {
  if (score >= 4) return 'bg-emerald-100';
  if (score === 3) return 'bg-emerald-50';
  if (score === 2) return 'bg-green-50';
  if (score === 1) return 'bg-lime-50';
  return 'bg-white';
}
